import { motion } from "framer-motion";
import { Trash2, Star, Eye, Edit, Upload, X } from "lucide-react";
import { useProductStore } from "../stores/useProductStore";
import { useState, useEffect } from "react";
import { getImageUrl, getProductImages } from "../lib/imageUtils";

const ProductsList = () => {
	const { deleteProduct, toggleFeaturedProduct, products, updateProduct, fetchAllProducts, loading } = useProductStore();
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [editingProduct, setEditingProduct] = useState(null);
	const [editingImages, setEditingImages] = useState([]); // שדה חדש לניהול תמונות בעריכה

	// טעינת מוצרים בהתחלה - כמו ב-HomePage
	useEffect(() => {
		console.log('📦 ProductsList: טוען מוצרים...');
		try {
			fetchAllProducts();
		} catch (error) {
			console.error('❌ ProductsList: שגיאה בטעינת מוצרים:', error);
		}
	}, [fetchAllProducts]);

	console.log("📦 ProductsList: products =", products);
	console.log("📦 ProductsList: loading =", loading);
	console.log("📦 ProductsList: products type =", typeof products);
	console.log("📦 ProductsList: products.length =", products?.length || 'undefined');

	const getDashboardProductImage = (product) => getProductImages(product)[0] || "/images/placeholder.svg";

	const handleViewDetails = (product) => {
		setSelectedProduct(product);
	};

	const closeModal = () => {
		setSelectedProduct(null);
	};

	const handleEditProduct = (product) => {
		setEditingProduct(product);
		setEditingImages(getProductImages(product));
	};

	const closeEditModal = () => {
		setEditingProduct(null);
		setEditingImages([]);
	};

	// נרמול SKU לפורמט תקין: SHA-<X>-NNN
	const normalizeSku = (category, rawSku) => {
		if (!category && !rawSku) return rawSku;
		const categoryMap = { necklaces: 'N', earrings: 'E', bracelets: 'B', rings: 'R' };
		const prefix = categoryMap[category] || (rawSku?.match(/^SHA-([A-Z])-\d{1,3}$/)?.[1] ?? 'X');
		// שליפת מספר מכל פורמט נפוץ (SHA-X-001, X001, N1, 1)
		const numMatch = String(rawSku || '').match(/(\d{1,3})$/);
		const num = numMatch ? parseInt(numMatch[1], 10) : null;
		if (!num) {
			// אם ה־SKU כבר תקין, נחזיר אותו כמות שהוא
			if (/^SHA-[A-Z]-\d{3}$/.test(String(rawSku))) return rawSku;
			return `SHA-${prefix}-001`;
		}
		return `SHA-${prefix}-${String(num).padStart(3, '0')}`;
	};

	// פונקציות לטיפול בעריכת תמונות
	const handleEditImageChange = (e) => {
		const files = Array.from(e.target.files);
		const remainingSlots = 5 - editingImages.length;
		const filesToAdd = files.slice(0, remainingSlots);

		filesToAdd.forEach(file => {
			const reader = new FileReader();
			reader.onloadend = () => {
				setEditingImages(prev => [...prev, reader.result]);
			};
			reader.readAsDataURL(file);
		});
	};

	const removeEditImage = (index) => {
		setEditingImages(prev => prev.filter((_, i) => i !== index));
	};

	const getEditImagePreview = (image) => {
		if (typeof image === "string" && image.startsWith("data:")) return image;
		return getImageUrl(image);
	};

	return (
		<>
			<motion.div
				className='bg-brand-primary-light shadow-lg rounded-lg overflow-hidden max-w-full m-auto'
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
			>
				<table className='min-w-full divide-y divide-gray-700'>
					<thead className='bg-gray-100 '>
						<tr>
							<th
								scope='col'
								className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
							>
								מוצר
							</th>
							<th
								scope='col'
								className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
							>
								מחיר
							</th>
							<th
								scope='col'
								className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
							>
								קטגוריה
							</th>
							<th
								scope='col'
								className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
							>
								מגדר גיל
							</th>
							<th
								scope='col'
								className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
							>
								סטטוס
							</th>
							<th
								scope='col'
								className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
							>
								מומלץ
							</th>
							<th
								scope='col'
								className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
							>
								פעולות
							</th>
						</tr>
					</thead>

					<tbody className='bg-gray-100 divide-y divide-gray-700'>
						{products && Array.isArray(products) && products.map((product) => (
							<tr key={product._id} className='hover:bg-gray-100'>
								<td className='px-4 py-4 '>
									<div className='flex items-center'>
										<div className='flex-shrink-0 h-10 w-20'>
											<img
												className='h-12 w-20  object-contain'
												src={getDashboardProductImage(product)}
												alt={product.name}
											/>
										</div>
										<div className='pl-0'>
											<div className='text-sm font-medium text-gray-500'>{product.name}</div>
											<div className='text-xs text-gray-500'>{product.sku}</div>
										</div>
									</div>
								</td>
								<td className='px-4 py-4 whitespace-nowrap'>
									<div className='text-sm text-gray-500'>₪{product.price.toFixed(2)}</div>
									{product.originalPrice && (
										<div className='text-xs text-gray-500 line-through'>₪{product.originalPrice.toFixed(2)}</div>
									)}
								</td>
								<td className='px-4 py-4 whitespace-nowrap'>
									<div className='text-sm text-gray-500'>{product.category}</div>
								</td>
								<td className='px-4 py-4 whitespace-nowrap'>
									<div className='text-sm text-gray-500'>{product.gender || 'לא מוגדר'}</div>
								</td>
								<td className='px-4 py-4 whitespace-nowrap'>
									<div className='flex flex-wrap gap-1'>
										{product.isNewProduct && (
											<span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'>
												חדש
											</span>
										)}
										{product.isOnSale && (
											<span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800'>
												במבצע
											</span>
										)}
										{product.inStock ? (
											<span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800'>
												במלאי
											</span>
										) : (
											<span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800'>
												אזל
											</span>
										)}
									</div>
								</td>
								<td className='px-4 py-4 whitespace-nowrap'>
									<button
										onClick={() => toggleFeaturedProduct(product._id)}
										className={`p-1 rounded-full ${
											product.isFeatured ? "bg-yellow-400 text-gray-900" : "bg-gray-600 text-gray-300"
										} hover:bg-yellow-500 transition-colors duration-200`}
									>
										<Star className='h-5 w-5' />
									</button>
								</td>
								<td className='px-4 py-4 whitespace-nowrap text-sm font-medium'>
									<div className='flex gap-2'>
										<button
											onClick={() => handleViewDetails(product)}
											className='text-blue-400 hover:text-blue-300'
											title="צפייה בפרטים"
										>
											<Eye className='h-5 w-5' />
										</button>
										<button
											onClick={() => handleEditProduct(product)}
											className='text-brand-primary hover:opacity-80'
											title="עריכת מוצר"
										>
											<Edit className='h-5 w-5' />
										</button>
										<button
											onClick={() => deleteProduct(product._id)}
											className='text-red-400 hover:text-red-300'
											title="מחיקת מוצר"
										>
											<Trash2 className='h-5 w-5' />
										</button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</motion.div>

			{/* Modal for product details */}
			{selectedProduct && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<div className='bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto'>
						<div className='flex justify-between items-center mb-4'>
							<h3 className='text-xl font-bold text-white'>פרטי המוצר</h3>
							<button
								onClick={closeModal}
								className='text-gray-400 hover:text-white'
							>
								×
							</button>
						</div>
						
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<img
									src={getDashboardProductImage(selectedProduct)}
									alt={selectedProduct.name}
									className='w-full h-48 object-cover rounded-lg'
								/>
							</div>
							
							<div className='space-y-3'>
								<div>
									<label className='text-sm font-medium text-gray-400'>שם המוצר:</label>
									<p className='text-white'>{selectedProduct.name}</p>
								</div>
								
								<div>
									<label className='text-sm font-medium text-gray-400'>SKU:</label>
									<p className='text-white'>{selectedProduct.sku}</p>
								</div>
								
								<div>
									<label className='text-sm font-medium text-gray-400'>תיאור:</label>
									<p className='text-white text-sm'>{selectedProduct.description}</p>
								</div>
								
								<div>
									<label className='text-sm font-medium text-gray-400'>מחיר:</label>
									<p className='text-white'>₪{selectedProduct.price.toFixed(2)}</p>
									{selectedProduct.originalPrice && (
										<p className='text-gray-400 text-sm line-through'>₪{selectedProduct.originalPrice.toFixed(2)}</p>
									)}
								</div>
								
								<div>
									<label className='text-sm font-medium text-gray-400'>קטגוריה:</label>
									<p className='text-white'>{selectedProduct.category}</p>
								</div>
								
								<div>
									<label className='text-sm font-medium text-gray-400'>חומר גלם:</label>
									<p className='text-white'>{selectedProduct.material}</p>
								</div>
								
								{selectedProduct.weight && (
									<div>
										<label className='text-sm font-medium text-gray-400'>משקל:</label>
										<p className='text-white'>{selectedProduct.weight} גרם</p>
									</div>
								)}
								
								{selectedProduct.length && (
									<div>
										<label className='text-sm font-medium text-gray-400'>אורך:</label>
										<p className='text-white'>{selectedProduct.length} ס&apos;מ</p>
									</div>
								)}
								
								{selectedProduct.stoneType && (
									<div>
										<label className='text-sm font-medium text-gray-400'>סוג אבן:</label>
										<p className='text-white'>{selectedProduct.stoneType}</p>
									</div>
								)}
								
								{selectedProduct.gender && (
									<div>
										<label className='text-sm font-medium text-gray-400'>מגדר:</label>
										<p className='text-white'>{selectedProduct.gender}</p>
									</div>
								)}
								
								{selectedProduct.stockLocation && (
									<div>
										<label className='text-sm font-medium text-gray-400'>מיקום במלאי:</label>
										<p className='text-white'>{selectedProduct.stockLocation}</p>
									</div>
								)}
								
								{selectedProduct.productStatus && (
									<div>
										<label className='text-sm font-medium text-gray-400'>סטטוס מוצר:</label>
										<p className='text-white'>{selectedProduct.productStatus}</p>
									</div>
								)}
								
								<div>
									<label className='text-sm font-medium text-gray-400'>כמות במלאי:</label>
									<p className='text-white'>{selectedProduct.quantity || selectedProduct.stock}</p>
								</div>
							</div>
						</div>
						
						<div className='mt-6 flex justify-end'>
							<button
								onClick={closeModal}
								className='px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700'
							>
								סגור
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Modal for editing product */}
			{editingProduct && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<div className='bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto'>
						<div className='flex justify-between items-center mb-4'>
							<h3 className='text-xl font-bold text-white'>עריכת מוצר: {editingProduct.name}</h3>
							<button
								onClick={closeEditModal}
								className='text-gray-400 hover:text-white text-2xl'
							>
								×
							</button>
						</div>
						<form
							onSubmit={async (e) => {
								e.preventDefault();
								const form = e.target;
							const normalizedSku = normalizeSku(form.category.value, form.sku.value);
							const safeImages = (editingImages || []).filter(img => typeof img === 'string' && !img.startsWith('data:'));
							const updatedData = {
									name: form.name.value,
									description: form.description.value,
									price: parseFloat(form.price.value),
									originalPrice: form.originalPrice.value ? parseFloat(form.originalPrice.value) : undefined,
									category: form.category.value,
								gender: form.gender.value,
									weight: form.weight.value ? parseFloat(form.weight.value) : undefined,
									length: form.length.value ? parseFloat(form.length.value) : undefined,
									width: form.width.value ? parseFloat(form.width.value) : undefined,
									material: form.material.value,
									wireThickness: form.wireThickness.value,
									earringThickness: form.earringThickness.value,
								sku: normalizedSku,
									barcode: form.barcode.value,
									stoneType: form.stoneType.value,
									quantity: form.quantity.value ? parseInt(form.quantity.value) : undefined,
									stockLocation: form.stockLocation.value,
									productStatus: form.productStatus.value,
									isNewProduct: form.isNewProduct.checked,
									isOnSale: form.isOnSale.checked,
									inStock: form.inStock.checked,
									isFeatured: form.isFeatured.checked,
								images: safeImages,
								imageUrl: safeImages.length > 0 ? safeImages[0] : (editingProduct.imageUrl || null)
								};
								await updateProduct(editingProduct._id, updatedData);
								closeEditModal();
							}}
						>
							<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
								{/* תמונות המוצר - עם אפשרות עריכה */}
								<div>
									<div className='flex items-center justify-between mb-3'>
										<h4 className='text-lg font-semibold text-white'>תמונות המוצר</h4>
										<span className='text-sm text-gray-400'>
											{editingImages.length}/5
										</span>
									</div>
									
									{/* כפתור הוספת תמונות */}
									{editingImages.length < 5 && (
										<div className='mb-4'>
											<input 
												type='file' 
												id='edit-images' 
												className='sr-only' 
												accept='image/*' 
												multiple
												onChange={handleEditImageChange} 
											/>
											<label
												htmlFor='edit-images'
												className='cursor-pointer bg-gray-700 py-2 px-3 border border-gray-600 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-300 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 inline-flex items-center'
											>
												<Upload className='h-4 w-4 mr-2' />
												הוסף תמונות
											</label>
										</div>
									)}

									{/* תצוגת תמונות עם אפשרות מחיקה */}
									<div className='grid grid-cols-2 gap-2'>
										{editingImages.map((image, index) => (
											<div key={index} className='relative group'>
												<img
													src={getEditImagePreview(image)}
													alt={`${editingProduct.name} - תמונה ${index + 1}`}
													className='w-full h-32 object-cover rounded-lg border border-gray-600'
												/>
												<button
													type='button'
													onClick={() => removeEditImage(index)}
													className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100'
												>
													<X className='h-3 w-3' />
												</button>
											</div>
										))}
									</div>
								</div>
								{/* פרטי המוצר */}
								<div className='space-y-4'>
									<div>
										<label className='block text-sm font-medium text-gray-400 mb-1'>שם המוצר</label>
										<input
											name='name'
											type='text'
											defaultValue={editingProduct.name}
											className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
										/>
									</div>
									<div>
										<label className='block text-sm font-medium text-gray-400 mb-1'>תיאור</label>
										<textarea
											name='description'
											defaultValue={editingProduct.description}
											rows={3}
											className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
										/>
									</div>
									<div className='grid grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>מחיר</label>
											<input
												name='price'
												type='number'
												step='0.01'
												defaultValue={editingProduct.price}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>מחיר מקורי</label>
											<input
												name='originalPrice'
												type='number'
												step='0.01'
												defaultValue={editingProduct.originalPrice || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
									</div>
									<div className='grid grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>קטגוריה</label>
											<select
												name='category'
												defaultValue={editingProduct.category}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											>
												<option value='necklaces'>שרשראות</option>
												<option value='earrings'>עגילים</option>
												<option value='rings'>טבעות</option>
												<option value='bracelets'>צמידים</option>
											</select>
										</div>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>מגדר</label>
											<select
												name='gender'
												defaultValue={editingProduct.gender || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											>
												<option value=''>לא מוגדר</option>
												<option value='women'>נשים</option>
												<option value='men'>גברים</option>
												<option value='unisex'>לשני המינים</option>
											</select>
										</div>
									</div>
									<div className='grid grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>משקל (גרם)</label>
											<input
												name='weight'
												type='number'
												defaultValue={editingProduct.weight || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>אורך (ס&apos;מ)</label>
											<input
												name='length'
												type='number'
												step='0.1'
												defaultValue={editingProduct.length || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
									</div>
									<div className='grid grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>חומר גלם</label>
											<input
												name='material'
												type='text'
												defaultValue={editingProduct.material || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>רוחב (ס&apos;מ)</label>
											<input
												name='width'
												type='number'
												step='0.1'
												defaultValue={editingProduct.width || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
									</div>
									<div className='grid grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>עובי חוט (מ&apos;מ)</label>
											<input
												name='wireThickness'
												type='text'
												defaultValue={editingProduct.wireThickness || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>עובי עגיל (מ&apos;מ)</label>
											<input
												name='earringThickness'
												type='text'
												defaultValue={editingProduct.earringThickness || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
									</div>
									<div className='grid grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>מק&apos;ט</label>
											<input
												name='sku'
												type='text'
												defaultValue={editingProduct.sku || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>ברקוד</label>
											<input
												name='barcode'
												type='text'
												defaultValue={editingProduct.barcode || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
									</div>
									<div>
										<label className='block text-sm font-medium text-gray-400 mb-1'>סוג אבן</label>
										<input
											name='stoneType'
											type='text'
											defaultValue={editingProduct.stoneType || ''}
											className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
										/>
									</div>
									<div className='grid grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>כמות במלאי</label>
											<input
												name='quantity'
												type='number'
												defaultValue={editingProduct.quantity || editingProduct.stock || 0}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
										<div>
											<label className='block text-sm font-medium text-gray-400 mb-1'>מיקום במלאי</label>
											<input
												name='stockLocation'
												type='text'
												defaultValue={editingProduct.stockLocation || ''}
												className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
											/>
										</div>
									</div>
									<div>
										<label className='block text-sm font-medium text-gray-400 mb-1'>סטטוס מוצר</label>
										<select
											name='productStatus'
											defaultValue={editingProduct.productStatus || ''}
											className='w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-emerald-500 focus:outline-none'
										>
											<option value=''>לא מוגדר</option>
											<option value='active'>פעיל</option>
											<option value='inactive'>לא פעיל</option>
											<option value='discontinued'>הופסק</option>
										</select>
									</div>
									{/* Checkboxes for status flags */}
									<div className='space-y-2'>
										<label className='flex items-center'>
											<input
																	name='isNewProduct'
					type='checkbox'
					defaultChecked={editingProduct.isNewProduct}
												className='mr-2 text-emerald-500 focus:ring-emerald-500'
											/>
											<span className='text-sm text-gray-300'>מוצר חדש</span>
										</label>
										<label className='flex items-center'>
											<input
												name='isOnSale'
												type='checkbox'
												defaultChecked={editingProduct.isOnSale}
												className='mr-2 text-emerald-500 focus:ring-emerald-500'
											/>
											<span className='text-sm text-gray-300'>במבצע</span>
										</label>
										<label className='flex items-center'>
											<input
												name='inStock'
												type='checkbox'
												defaultChecked={editingProduct.inStock}
												className='mr-2 text-emerald-500 focus:ring-emerald-500'
											/>
											<span className='text-sm text-gray-300'>במלאי</span>
										</label>
										<label className='flex items-center'>
											<input
												name='isFeatured'
												type='checkbox'
												defaultChecked={editingProduct.isFeatured}
												className='mr-2 text-emerald-500 focus:ring-emerald-500'
											/>
											<span className='text-sm text-gray-300'>מוצר מומלץ</span>
										</label>
									</div>
								</div>
							</div>
							<div className='mt-6 flex justify-end space-x-3'>
								<button
									onClick={closeEditModal}
									className='px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700'
								>
									ביטול
								</button>
								<button
									type='submit'
									className='px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700'
								>
									שמור שינויים
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</>
	);
};
export default ProductsList;
